/**
 * Pack-level analytics for Risk Analytics (incident groups + site policies).
 *
 * Semantics:
 * - Pack for a group is derived from the joined policy row when `policy_id` matches a listed policy
 *   (`derivePackIdentity` on that Policy). Groups whose `policy_id` is missing or not found in the
 *   policy list are bucketed as unknown — we do not infer pack from free-text policy names alone.
 * // TODO: extend list_incident_groups API with policy_type when policy row is missing from cache
 *   so attribution can improve without guessing from incident_type.
 */

import type { IncidentGroup } from '../../services/incidentsApi';
import type { Policy } from '../../services/policiesApi';
import type { Zone } from '../../services/zonesApi';
import { derivePackIdentity } from '../../domain/policies/policyPresentation';
import { PACK_BY_KEY, PACK_DISPLAY_ORDER, type PackKey } from '../../domain/policies/policyPacks';
import { isIncidentGroupRecurring } from '../../domain/incidents/incidentPatternSemantics';

export const PACK_COVERAGE_UNKNOWN_KEY = 'unknown' as const;

export type PackCoverageUnknownKey = typeof PACK_COVERAGE_UNKNOWN_KEY;

const RESOLVED_GROUP_STATUSES = new Set(['closed', 'suppressed']);

export function isIncidentGroupOpen(group: IncidentGroup | null | undefined): boolean {
  const status = String(group?.status || '').trim().toLowerCase();
  if (!status) return true;
  return !RESOLVED_GROUP_STATUSES.has(status);
}

export function buildPolicyByIdMap(policies: Policy[] | null | undefined): Map<string, Policy> {
  const map = new Map<string, Policy>();
  (policies || []).forEach((p) => {
    const id = String(p?.policy_id || '').trim();
    if (id) map.set(id, p);
  });
  return map;
}

export function buildZonesByIdMap(zones: Zone[] | null | undefined): Map<string, Zone> {
  const map = new Map<string, Zone>();
  (zones || []).forEach((z) => {
    const id = String(z?.zone_id || '').trim();
    if (id) map.set(id, z);
  });
  return map;
}

export function resolvePackKeyForGroup(
  group: IncidentGroup,
  policyById: Map<string, Policy>,
  lookups: { zonesById?: Map<string, Zone> },
): PackKey | PackCoverageUnknownKey {
  const pid = String(group?.policy_id || '').trim();
  if (!pid) return PACK_COVERAGE_UNKNOWN_KEY;
  const policy = policyById.get(pid);
  if (!policy) return PACK_COVERAGE_UNKNOWN_KEY;
  return derivePackIdentity(policy, { zonesById: lookups.zonesById });
}

export type PackCoverageKpis = {
  /** Distinct packs that have at least one non-deleted policy in the site catalog. */
  packsWithPolicies: number;
  activePolicies: number;
  packsWithOpenIncidents: number;
  policiesTriggeredInWindow: number;
};

export type PackCoverageChartRow = {
  packKey: string;
  packLabel: string;
  openCount: number;
};

export type PackCoverageDrilldownRow = {
  packKey: string;
  packLabel: string;
  policiesActive: number;
  policiesTotal: number;
  openIncidentGroups: number;
  recurringOrHotspots: number;
  lastTriggerAt: string | null;
};

export type PackCoverageAnalytics = {
  kpis: PackCoverageKpis;
  openIncidentsByPack: PackCoverageChartRow[];
  drilldownRows: PackCoverageDrilldownRow[];
};

function parseSeenTs(group: IncidentGroup): number {
  const raw = String(group?.last_seen || group?.last_seen_at || group?.first_seen || '').trim();
  if (!raw) return 0;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
}

function isRecurringGroup(group: IncidentGroup): boolean {
  return isIncidentGroupRecurring(group);
}

function isPolicyRowDeployed(p: Policy): boolean {
  if (String(p?.status || '').toLowerCase() === 'deleted') return false;
  return true;
}

function isPolicyActive(p: Policy): boolean {
  if (String(p?.status || '').toLowerCase() === 'deleted') return false;
  if (String(p?.status || '').toLowerCase() !== 'active') return false;
  if (p.enabled === false) return false;
  return true;
}

/**
 * Aggregates pack coverage for Risk Analytics from incident groups and the site policy catalog.
 */
export function computePackCoverageAnalytics(args: {
  groups: IncidentGroup[];
  policies: Policy[];
  zones?: Zone[];
}): PackCoverageAnalytics {
  const { groups, policies } = args;
  const zonesById = buildZonesByIdMap(args.zones);
  const policyById = buildPolicyByIdMap(policies);
  const lookups = { zonesById };

  const packKeysFromPolicies = new Set<PackKey | PackCoverageUnknownKey>();
  policies.forEach((p) => {
    if (!isPolicyRowDeployed(p)) return;
    packKeysFromPolicies.add(derivePackIdentity(p, lookups));
  });
  const packsWithPolicies = packKeysFromPolicies.size;

  const activePolicies = (policies || []).filter((p) => isPolicyActive(p)).length;

  const policiesTriggeredIds = new Set<string>();
  (groups || []).forEach((g) => {
    const pid = String(g?.policy_id || '').trim();
    if (pid) policiesTriggeredIds.add(pid);
  });
  const policiesTriggeredInWindow = policiesTriggeredIds.size;

  const openGroups = (groups || []).filter((g) => isIncidentGroupOpen(g));
  const packsWithOpen = new Set<string>();
  openGroups.forEach((g) => {
    packsWithOpen.add(resolvePackKeyForGroup(g, policyById, lookups));
  });
  const packsWithOpenIncidents = packsWithOpen.size;

  const openByPack = new Map<string, number>();
  openGroups.forEach((g) => {
    const key = resolvePackKeyForGroup(g, policyById, lookups);
    openByPack.set(key, (openByPack.get(key) || 0) + 1);
  });

  const openIncidentsByPack: PackCoverageChartRow[] = Array.from(openByPack.entries())
    .map(([packKey, openCount]) => ({
      packKey,
      packLabel:
        packKey === PACK_COVERAGE_UNKNOWN_KEY
          ? 'Not linked to a pack'
          : PACK_BY_KEY[packKey as PackKey]?.label || packKey,
      openCount,
    }))
    .sort((a, b) => b.openCount - a.openCount);

  const policiesPerPack = new Map<string, { total: number; active: number }>();
  (policies || []).forEach((p) => {
    if (!isPolicyRowDeployed(p)) return;
    const pk = derivePackIdentity(p, lookups);
    const cur = policiesPerPack.get(pk) || { total: 0, active: 0 };
    cur.total += 1;
    if (isPolicyActive(p)) cur.active += 1;
    policiesPerPack.set(pk, cur);
  });

  const recurringPerPack = new Map<string, number>();
  const lastTriggerPerPack = new Map<string, number>();
  (groups || []).forEach((g) => {
    const pk = resolvePackKeyForGroup(g, policyById, lookups);
    if (isRecurringGroup(g)) recurringPerPack.set(pk, (recurringPerPack.get(pk) || 0) + 1);
    const ts = parseSeenTs(g);
    if (ts > 0) lastTriggerPerPack.set(pk, Math.max(lastTriggerPerPack.get(pk) || 0, ts));
  });

  const candidatePackKeys = new Set<string>([
    ...Array.from(policiesPerPack.keys()),
    ...Array.from(openByPack.keys()),
    ...Array.from(recurringPerPack.keys()),
    ...Array.from(lastTriggerPerPack.keys()),
  ]);

  const orderedRowKeys: string[] = [];
  PACK_DISPLAY_ORDER.forEach((pk) => {
    if (candidatePackKeys.has(pk)) orderedRowKeys.push(pk);
  });
  candidatePackKeys.forEach((pk) => {
    if (pk === PACK_COVERAGE_UNKNOWN_KEY) return;
    if (!orderedRowKeys.includes(pk)) orderedRowKeys.push(pk);
  });
  if (candidatePackKeys.has(PACK_COVERAGE_UNKNOWN_KEY)) orderedRowKeys.push(PACK_COVERAGE_UNKNOWN_KEY);

  const drilldownRows: PackCoverageDrilldownRow[] = orderedRowKeys.map((pk) => {
    const counts = policiesPerPack.get(pk) || { total: 0, active: 0 };
    return {
      packKey: pk,
      packLabel:
        pk === PACK_COVERAGE_UNKNOWN_KEY
          ? 'Not linked to a pack'
          : PACK_BY_KEY[pk as PackKey]?.label || pk,
      policiesActive: counts.active,
      policiesTotal: counts.total,
      openIncidentGroups: openByPack.get(pk) || 0,
      recurringOrHotspots: recurringPerPack.get(pk) || 0,
      lastTriggerAt: lastTriggerPerPack.has(pk)
        ? new Date(lastTriggerPerPack.get(pk) || 0).toISOString()
        : null,
    };
  });

  return {
    kpis: {
      packsWithPolicies,
      activePolicies,
      packsWithOpenIncidents,
      policiesTriggeredInWindow,
    },
    openIncidentsByPack,
    drilldownRows,
  };
}

export type IncidentLikeForPack = {
  policy_id?: string | null;
  policy_name?: string | null;
  policy_type?: string | null;
  triggered_by?: { policy_id?: string; policy_name?: string; policy_type?: string } | null;
};

/**
 * Secondary drawer labels: pack from catalog policy when possible; policy line from row or policy name.
 */
export function presentPackPolicyForIncidentLike(
  row: IncidentLikeForPack | null | undefined,
  policies: Policy[] | null | undefined,
  options?: { zonesById?: Map<string, Zone> },
): { packLabel: string; policyLabel: string } {
  if (!row) return { packLabel: '', policyLabel: '' };
  const pid = String(row.policy_id || row.triggered_by?.policy_id || '').trim();
  const policyById = buildPolicyByIdMap(policies);
  const policy = pid ? policyById.get(pid) : undefined;
  const policyLabel = String(
    row.policy_name || row.triggered_by?.policy_name || policy?.name || pid || '',
  ).trim();

  if (policy) {
    const pk = derivePackIdentity(policy, { zonesById: options?.zonesById });
    const packLabel = PACK_BY_KEY[pk]?.label || pk;
    return { packLabel, policyLabel: policyLabel || policy.name || policy.policy_id };
  }

  return { packLabel: '', policyLabel: policyLabel || '' };
}
